import {createAction, createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'
import {eq} from '@shared/helpers/lang'

import {OrderSheetPageSetType, PointPaymentCase} from '@web/core/constants'

import api from './apis'
import {
    PayDueChange,
    PayDueChangeFailCase,
    PayDueChangeFailCaseType,
    TradeableStatus,
    UserInfoChangeFail,
} from './constants'
import {
    GetUserTradeableResponse,
    PayLaterError,
    PayLaterMemberInfoResponse,
    PayLaterReloadResponse,
    PayLaterUserSignUpStatusResponse,
    UpdatePayLaterStatusParams,
} from './interfaces'

export {default as api} from './apis'

/** 후불결제 AsyncThunk */

/** 후불결제 포인트 변경 */
export const changePayLaterPoint = createAction<number>('payLater/change')

/**
 * 후불결제 포인트 초기화
 **/
export const resetPayLaterPoint = createAction('payLater/reset')

/**
 * 사용가능한 전체 금액을 사용합니다.
 */
export const usingAllPayLaterPoint = createAction('payLater/usingAll')

/**
 * 후불결제 사용 action
 **/
export const enablePayLaterPoint = createAction<boolean>('payLater/enable')

/**
 * 후불결제 정보 재조회
 */
export const getPayLater = createAsyncThunk<
    PayLaterReloadResponse,
    OrderSheetPageSetType,
    {
        rejectValue: PayLaterError
    }
>('payLater/reload', async (orderSheetPageSet, {rejectWithValue}) => {
    try {
        const result = await api.getPayLater(orderSheetPageSet)
        return result
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

/**
 * 후불결제 유저 상태 조회
 */
export const getPayLaterUserStatus = createAsyncThunk<
    PayLaterUserSignUpStatusResponse,
    void,
    {
        rejectValue: string
    }
>('payLater/getPayLaterUserStatus', async (_, {rejectWithValue}) => {
    try {
        return await api.getPayLaterUserStatus()
    } catch {
        return rejectWithValue('failed to get user signup status')
    }
})

/**
 * 후불결제 사용자상태 조회(light) API를 사용하여
 * 납부일변경이 필요한 경우 true 반환
 */
export const validatePayLaterDueDay = createAsyncThunk<
    {shouldPayDueChange: boolean},
    void,
    {
        rejectValue: string
    }
>('payLater/validatePayLaterDueDay', async (_, {rejectWithValue}) => {
    try {
        const result = await api.getLightPayLaterUserStatus()

        if (eq(result.tradeableStatus, TradeableStatus.UNCHANGED_PAYDUE)) {
            return {
                shouldPayDueChange: true,
            }
        }

        return {
            shouldPayDueChange: false,
        }
    } catch {
        return rejectWithValue('일시적으로 후불결제를 사용할 수 없습니다. 잠시 후 다시 시도해주세요.')
    }
})

/**
 * 후불결제 통합 변경 action (납부일, 이메일, 납부계좌)
 */
export const updatePayLaterStatus = createAsyncThunk<
    {
        isSuccess: boolean
        failCase: Maybe<PayDueChangeFailCaseType>
        failReason: string
    },
    UpdatePayLaterStatusParams,
    {
        rejectValue: string
    }
>('payLater/updatePayLaterStatus', async ({accountKey, email, newPayDue}, {rejectWithValue}) => {
    try {
        const {newPayDue: changedNewPayDue, failedReason} = await api.updatePayLaterStatus({
            accountKey,
            email,
            newPayDue,
        })

        const reasonCodeList = failedReason.map((reasonObj) => reasonObj.code)

        if (changedNewPayDue === 15 || reasonCodeList.includes(UserInfoChangeFail.PAYDUE_CANNOT_REQUEST_SAME_PAYDUE)) {
            return {
                isSuccess: true,
                failCase: null,
                failReason: '',
            }
        } else if (reasonCodeList.includes(UserInfoChangeFail.PAYDUE_INVALID_TIME)) {
            return {
                isSuccess: false,
                failCase: PayDueChangeFailCase.INVALID_TIME,
                failReason:
                    failedReason.find((reasonObj) => reasonObj.code === UserInfoChangeFail.PAYDUE_INVALID_TIME)
                        ?.reason ?? '00시~07시',
            }
        } else if (reasonCodeList.includes(UserInfoChangeFail.PAYDUE_INVALID_USER_STATUS)) {
            const reason =
                failedReason.find((reasonObj) => reasonObj.code === UserInfoChangeFail.PAYDUE_INVALID_USER_STATUS)
                    ?.reason ?? ''

            const isOverdue = reason.includes('OVERDUE')

            return {
                isSuccess: false,
                failCase: isOverdue ? PayDueChangeFailCase.OVERDUE : PayDueChangeFailCase.SUSPEBNDED,
                failReason: '',
            }
        }

        return {
            isSuccess: false,
            failCase: null,
            failReason: '',
        }
    } catch {
        return rejectWithValue('후불결제 사용자상태 변경에 실패했습니다.')
    }
})

/**
 * 후불결제 납부일 조회 action
 */
export const getPayLaterPayDueDay = createAsyncThunk<
    {
        month: number
        day: number
    },
    void,
    {
        rejectValue: string
    }
>('payLater/getPayLaterPayDueDay', async (_, {rejectWithValue}) => {
    try {
        const {changeType, first, second} = await api.getPayLaterPayDueDay()

        if (eq(changeType, PayDueChange.IMMEDIATE)) {
            const [, month, day] = first.dueDate.split('-')
            return {
                month: Number(month),
                day: Number(day),
            }
        }

        const [, month, day] = second.dueDate.split('-')
        return {
            month: Number(month),
            day: Number(day),
        }
    } catch {
        return rejectWithValue('후불결제 납부일 조회에 실패했습니다.')
    }
})

/**
 * 후불결제 가입대상자 조회 action
 */
export const getCheckCandidate = createAsyncThunk<
    boolean,
    void,
    {
        rejectValue: string
    }
>('payLater/getCheckCandidate', async (_, {rejectWithValue}) => {
    try {
        const isCandidate = await api.getCheckCandidate()

        return isCandidate
    } catch {
        return rejectWithValue(PointPaymentCase.PAY_LATER_CANDIDATE_STATUS_FAIL)
    }
})

/**
 * 후불결제 가입미대상자 알림신청 조회 action
 */
export const getNotificationStatus = createAsyncThunk<boolean, void, {rejectValue: string}>(
    'payLater/getNotificationStatus',
    async (_, {rejectWithValue}) => {
        try {
            const {enableYn} = await api.getNotificationStatus()

            return enableYn
        } catch {
            return rejectWithValue('알림신청여부 조회에 실패했습니다.')
        }
    },
)

/**
 * 후불결제 가입미대상자 알림신청 변경 action
 */
export const updateNotificationStatus = createAsyncThunk<boolean, boolean, {rejectValue: string}>(
    'payLater/updateNotificationStatus',
    async (enableYnParams, {rejectWithValue}) => {
        try {
            const {enableYn} = await api.updateNotificationStatus({
                enableYn: enableYnParams,
            })

            return enableYn
        } catch {
            return rejectWithValue('알림 신청에 실패하였습니다. 잠시 후 다시 시도해주세요.')
        }
    },
)

/**
 * 주문서 진입 시 후불결제 정보 조회
 *
 * 51. 후불결제 사용자 정보 조회 (light)
 * @see https://wiki.fin.navercorp.com/spaces/FIN/pages/8059775/51.+PL+%EC%82%AC%EC%9A%A9%EC%9E%90+%EC%A0%95%EB%B3%B4+%EC%A1%B0%ED%9A%8C+API+light+%EB%B2%84%EC%A0%84
 * 63. 후불결제 거래 가능여부 조회
 * @see https://wiki.fin.navercorp.com/spaces/FIN/pages/8059787/63.+PL+%EA%B1%B0%EB%9E%98+%EA%B0%80%EB%8A%A5%EC%97%AC%EB%B6%80+%EC%A1%B0%ED%9A%8C+API
 */
export const getPayLaterInfo = createAsyncThunk<
    {
        info: PayLaterMemberInfoResponse
        tradeable: GetUserTradeableResponse
    },
    void,
    {rejectValue: unknown}
>('payLater/getPayLaterInfo', async (_, {rejectWithValue}) => {
    try {
        const [info, tradeable] = await Promise.all([api.getLightPayLaterUserStatus(), api.getUserTradeable()])

        return {info, tradeable}
    } catch (error) {
        return rejectWithValue(error)
    }
})
